import React, { useMemo } from 'react';
import { editionsData, Edition } from '../data/editionData';
import { Check, Plus } from 'lucide-react';
import { SmartAskAmountsInfo } from './SmartAskAmountsInfo';
interface EditionSelectorProps {
  selectedEdition: string | null;
  onSelectEdition: (editionName: string) => void;
  includeGO: boolean;
  includeVM: boolean;
  onToggleGO: () => void;
  onToggleVM: () => void;
}
export function EditionSelector({
  selectedEdition,
  onSelectEdition,
  includeGO,
  includeVM,
  onToggleGO,
  onToggleVM
}: EditionSelectorProps) {
  // Check if selected edition includes Smart Ask Amounts
  const hasSmartAskAmounts = useMemo(() => {
    if (!selectedEdition) return false;
    const edition = editionsData.find(e => e.name === selectedEdition);
    if (!edition) return false;
    return edition.productFeatures.some(pf => pf.features.includes('Smart Ask Amounts'));
  }, [selectedEdition]);
  return <div className="space-y-6" data-id="element-1" data-mp="3l2scdlonzmqq">
      <div data-id="element-2" data-mp="3kzwe1w8wmw1r">
        <h3 className="text-sm font-semibold text-gray-900 mb-2" data-id="element-3" data-mp="3kf789ak7hbpn">
          Select an Edition
        </h3>
        <p className="text-sm text-gray-500 mb-4" data-id="element-4" data-mp="3fg44rujvjdjs">
          Choose an edition to view its included products, features, and field
          requirements
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-5" data-mp="3tdsnowudsam7">
        {editionsData.map(edition => <EditionCard key={edition.name} edition={edition} isSelected={selectedEdition === edition.name} onSelect={() => onSelectEdition(edition.name)} data-id="element-6" data-mp="78oxj8mt8p5r" />)}
      </div>

      {/* GO and VM Toggle Buttons */}
      {selectedEdition && <div className="border-t border-gray-200 pt-6" data-id="element-7" data-mp="bznjanqkifge">
          <h3 className="text-sm font-semibold text-gray-900 mb-3" data-id="element-8" data-mp="c071m5k4r5uy">
            Optional Add-ons
          </h3>
          <p className="text-sm text-gray-500 mb-4" data-id="element-9" data-mp="px3s25wc6lbb">
            Include additional products to see their field requirements
            alongside your edition
          </p>
          <div className="flex flex-wrap gap-3" data-id="element-10" data-mp="3etd0ocya5wwa">
            <button onClick={onToggleGO} className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg border-2 font-medium text-sm transition-all ${includeGO ? 'bg-[#0047AB] border-[#0047AB] text-white hover:bg-[#003580]' : 'bg-white border-gray-300 text-gray-700 hover:border-[#0047AB] hover:text-[#0047AB]'}`} data-id="element-11" data-mp="3jqwni191nhzu">
              {includeGO ? <Check className="w-4 h-4" data-id="element-12" data-mp="6c9rjofv85fz" /> : <Plus className="w-4 h-4" data-id="element-13" data-mp="6aoxdzxgqoin" />}
              GO
            </button>
            <button onClick={onToggleVM} className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg border-2 font-medium text-sm transition-all ${includeVM ? 'bg-[#0047AB] border-[#0047AB] text-white hover:bg-[#003580]' : 'bg-white border-gray-300 text-gray-700 hover:border-[#0047AB] hover:text-[#0047AB]'}`} data-id="element-14" data-mp="bbus23aoolut">
              {includeVM ? <Check className="w-4 h-4" data-id="element-15" data-mp="29lk6b13d23jx" /> : <Plus className="w-4 h-4" data-id="element-16" data-mp="24ubrtqn6brzw" />}
              VM
            </button>
          </div>
        </div>}

      {selectedEdition && <SelectedEditionDetails edition={editionsData.find(e => e.name === selectedEdition)!} includeGO={includeGO} includeVM={includeVM} data-id="element-17" data-mp="1zswaa8k7g3m9" />}

      {/* Smart Ask Amounts Info */}
      {hasSmartAskAmounts && <SmartAskAmountsInfo data-id="element-18" data-mp="2nkbfqa7a311q" />}
    </div>;
}
interface EditionCardProps {
  edition: Edition;
  isSelected: boolean;
  onSelect: () => void;
}
function EditionCard({
  edition,
  isSelected,
  onSelect
}: EditionCardProps) {
  return <button onClick={onSelect} className={`relative text-left p-4 rounded-lg border-2 transition-all ${isSelected ? 'border-[#0047AB] bg-[#EFF6FF] shadow-md' : 'border-gray-200 bg-white hover:border-gray-300 hover:shadow-sm'}`} data-id="element-19" data-mp="2ej3ky23z0ice">
      {isSelected && <div className="absolute top-3 right-3" data-id="element-20" data-mp="2r15wljpln5fz">
          <div className="w-6 h-6 bg-[#0047AB] rounded-full flex items-center justify-center" data-id="element-21" data-mp="2qzbai22ld7ab">
            <Check className="w-4 h-4 text-white" data-id="element-22" data-mp="2vu7fs3wu9r5d" />
          </div>
        </div>}

      <div className="pr-8" data-id="element-23" data-mp="2i2aog22zvla1">
        <h4 className="text-base font-semibold text-gray-900" data-id="element-24" data-mp="2m2b4fr5qs4j7">
          {edition.name}
        </h4>
        {edition.subtitle && <p className="text-xs text-gray-500 mt-0.5" data-id="element-25" data-mp="39rnojm2lxx0a">{edition.subtitle}</p>}
        <p className="text-sm text-gray-600 mt-2" data-id="element-26" data-mp="3eridvl6qiej8">{edition.description}</p>
      </div>
    </button>;
}
interface SelectedEditionDetailsProps {
  edition: Edition;
  includeGO: boolean;
  includeVM: boolean;
}
function SelectedEditionDetails({
  edition,
  includeGO,
  includeVM
}: SelectedEditionDetailsProps) {
  return <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-27" data-mp="1pb0shy0260a1">
      <h4 className="text-base font-semibold text-gray-900 mb-4" data-id="element-28" data-mp="1uavmwbj1l7zu">
        {edition.name} {edition.subtitle && `(${edition.subtitle})`} - Included
        Features
      </h4>

      <div className="space-y-4" data-id="element-29" data-mp="1lvzak9pe9vmw">
        {edition.productFeatures.map(pf => <div key={pf.product} data-id="element-30" data-mp="1hs9r1ynku48q">
            <h5 className="text-sm font-semibold text-gray-900 mb-2" data-id="element-31" data-mp="1vs2cpfk8xu9u">
              {pf.product}
            </h5>
            <div className="flex flex-wrap gap-2" data-id="element-32" data-mp="1qspsz3oo93bh">
              {pf.features.map(feature => <span key={feature} className="inline-flex items-center px-3 py-1 bg-[#E0F2FE] text-[#0047AB] text-sm font-medium rounded-full" data-id="element-33" data-mp="1333p42xctzx5">
                  {feature}
                </span>)}
            </div>
          </div>)}

        {/* Show GO if included */}
        {includeGO && <div data-id="element-34" data-mp="180wzl85oxg38">
            <h5 className="text-sm font-semibold text-gray-900 mb-2" data-id="element-35" data-mp="kcvuyabmujt3">GO</h5>
            <div className="flex flex-wrap gap-2" data-id="element-36" data-mp="kasv6ykohcb6">
              <span className="inline-flex items-center px-3 py-1 bg-[#E0F2FE] text-[#0047AB] text-sm font-medium rounded-full" data-id="element-37" data-mp="fd15mb7ia2a5">
                Core
              </span>
            </div>
          </div>}

        {/* Show VM if included */}
        {includeVM && <div data-id="element-38" data-mp="2p5lirj6aw9yx">
            <h5 className="text-sm font-semibold text-gray-900 mb-2" data-id="element-39" data-mp="2p76cx7opdqw9">VM</h5>
            <div className="flex flex-wrap gap-2" data-id="element-40" data-mp="2t7gqp6iqlllo">
              <span className="inline-flex items-center px-3 py-1 bg-[#E0F2FE] text-[#0047AB] text-sm font-medium rounded-full" data-id="element-41" data-mp="2t6xb3wgk79at">
                Core
              </span>
            </div>
          </div>}
      </div>
    </div>;
}